"use client";

import { useRef, useState } from "react";
import { Loader, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const names = Array.from(files).slice(0, 8).map(f => f.name);
    setFileNames(names);
  };
  if (isSubmitted && !submitError) {
    return <div className="border-t border-b border-border-subtle py-12 px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-accent-foreground mb-4 font-mono">Application Received</p>
        <p className="text-2xl text-foreground font-default italic mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-foreground text-foreground hover:bg-foreground hover:text-background uppercase tracking-[0.2em] text-xs font-default">
          Submit Another Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="name" rules={{
      required: "Your full name is required",
      minLength: {
        value: 2,
        message: "Minimum 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="group">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-mono group-focus-within:text-accent-foreground transition-colors duration-300">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-accent-foreground transition-colors duration-300 text-base font-default" />
            </FormControl>
            <p className="text-xs italic text-muted-foreground font-default mt-1">Please provide your legal name.</p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="group">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-mono group-focus-within:text-accent-foreground transition-colors duration-300">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-accent-foreground transition-colors duration-300 text-base font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem className="group">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-mono group-focus-within:text-accent-foreground transition-colors duration-300">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 000-0000" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-accent-foreground transition-colors duration-300 text-base font-default" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="position" rules={{
      required: "Please select a position"
    }} render={({
      field
    }) => <FormItem className="group">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-mono group-focus-within:text-accent-foreground transition-colors duration-300">
              Desired Position
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 h-11 text-foreground focus:ring-0 focus-visible:ring-0 focus-visible:border-accent-foreground data-[placeholder]:text-muted-foreground/50 text-base font-default w-full">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-card border-border rounded-lg font-default">
                <SelectItem value="DirectSupport">Direct Support Professional</SelectItem>
                <SelectItem value="CaseManager">Case Manager</SelectItem>
                <SelectItem value="Administrative">Administrative Support</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="experience" rules={{
      required: "Please describe your experience",
      minLength: {
        value: 10,
        message: "Please provide more detail"
      }
    }} render={({
      field
    }) => <FormItem className="group">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-mono group-focus-within:text-accent-foreground transition-colors duration-300">
              Professional Experience
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your background in social services or healthcare." rows={4} {...field} className="border-0 border-b border-border bg-transparent px-0 py-2 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-accent-foreground transition-colors duration-300 text-base font-default resize-none min-h-[6rem]" />
            </FormControl>
            <p className="text-xs italic text-muted-foreground font-default mt-1">
              Highlight your experience with DDS-qualified services.
            </p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="resume" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-mono">
              Upload Resume (optional)
            </FormLabel>
            <FormControl>
              <div onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }} onDragLeave={() => setIsDragging(false)} onDrop={e => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
          field.onChange(e.dataTransfer.files);
        }} onClick={() => fileInputRef.current?.click()} className={`border border-dashed ${isDragging ? "border-accent-foreground bg-accent/5" : "border-border-subtle"} rounded-lg py-12 px-6 text-center cursor-pointer transition-colors duration-300 hover:border-accent-foreground hover:bg-accent/5`}>
                <Upload className="mx-auto mb-4 size-5 text-accent-foreground" />
                <p className="text-[0.7rem] uppercase tracking-[0.3em] text-accent-foreground font-mono">
                  Drag Resume Here — Or Browse
                </p>
                {fileNames.length > 0 && <div className="mt-4 space-y-1">
                    {fileNames.map((name, i) => <p key={i} data-index={i} className="text-xs italic text-foreground font-default truncate">
                        {name}
                      </p>)}
                  </div>}
                <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" multiple className="hidden" onChange={e => {
            handleFiles(e.target.files);
            field.onChange(e.target.files);
          }} />
              </div>
            </FormControl>
            <p className="text-xs italic text-muted-foreground font-default mt-2">
              PDF or Word documents only.
            </p>
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 bg-destructive/5 px-4 py-3 rounded-lg">
          <p className="text-sm text-destructive font-default italic">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isSubmitting} className="bg-accent-foreground text-white hover:bg-accent-foreground/90 rounded-lg uppercase tracking-[0.25em] text-xs font-mono px-8 h-12">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting
            </> : <>Submit Application →</>}
        </Button>
      </div>
    </Form>;
}
export default function EmploymentApplication() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="4279" id="employment-application" className="bg-background text-foreground py-32 md:py-48 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[radial-gradient(circle_at_20%_30%,var(--foreground)_1px,transparent_1px)] bg-[length:24px_24px]" />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-32">
              <p className="text-[0.7rem] uppercase tracking-[0.35em] text-accent-foreground font-mono mb-10">
                § 04 — Careers
              </p>

              <h1 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground mb-10">
                Join our <em className="italic font-light text-accent-foreground">dedicated team.</em>
              </h1>

              <div className="w-16 h-px bg-accent-foreground mb-10" />

              <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/85 mb-8">
                Waters Well Inc. is seeking compassionate professionals to support our mission in Connecticut. Apply today to make a meaningful impact in our community.
              </p>

              <p className="font-default italic text-sm text-muted-foreground leading-relaxed border-l border-accent-foreground/40 pl-4">
                We are an equal opportunity employer committed to providing high-quality, person-centered support services.
              </p>

              <div className="hidden lg:block mt-16 pt-8 border-t border-border">
                <p className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-mono mb-3">
                  Headquarters
                </p>
                <p className="font-default text-sm text-foreground/80 leading-relaxed">
                  Waters Well Inc.<br />
                  Connecticut DDS Qualified Provider<br />
                  Hartford, CT
                </p>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <FormProvider formKey="employment_application_form_careers" formId="employment_application_form_careers" sectionName="employment-application" defaultValues={{
            name: "",
            email: "",
            phone: "",
            position: "",
            experience: "",
            resume: null
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            position: {
              required: true
            },
            experience: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Application" successMessage="Your application has been received. Our hiring team will review your qualifications and contact you shortly." errorMessage="We were unable to process your application. Please verify your details and try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}